import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowDownRight,
  X,
  ArrowUpRight,
  BadgeCheck,
  Layers,
  Moon,
  RefreshCw,
  Sun,
  Wallet,
  WifiOff,
} from "lucide-react";
import useInvestorReport from "./useInvestorReport";
import ContainerSelect from "./ContainerSelect";
import "./InvestorReport.css";

const PERIOD_KEYS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "year", label: "This year" },
  /* No calendar boundary - the whole record, from the overall start date. It
     reads the same as This year until the year rolls over, which is correct
     rather than redundant: on 1 January the year resets and this does not. */
  { key: "overall", label: "Overall" },
];

/* ------------------------------- formatters ------------------------------- */

const abs = (v, dp = 2) =>
  Math.abs(v || 0).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

const sym = (ccy) => ({ USD: "$", EUR: "€", GBP: "£", JPY: "¥" }[ccy] || `${ccy} `);
const money = (v, ccy, dp = 2) => `${(v || 0) < 0 ? "−" : ""}${sym(ccy)}${abs(v, dp)}`;
const signed = (v, ccy, dp = 2) => `${(v || 0) < 0 ? "−" : "+"}${sym(ccy)}${abs(v, dp)}`;
const compact = (v, ccy) => {
  const a = Math.abs(v || 0);
  const s = (v || 0) < 0 ? "−" : "";
  if (a >= 1e6) return `${s}${sym(ccy)}${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}${sym(ccy)}${(a / 1e3).toFixed(1)}K`;
  return `${s}${sym(ccy)}${a.toFixed(0)}`;
};
const pctStr = (v, dp = 2) => `${(v || 0) < 0 ? "−" : "+"}${Math.abs(v || 0).toFixed(dp)}%`;
const plain = (v, dp = 1) => `${(v || 0).toFixed(dp)}%`;
const dir = (v) => (v > 0 ? "up" : v < 0 ? "down" : "flat");

/**
 * THE TWO SETTLE LIMITS, by the same formula the server uses — `ceilings` in
 * mt5/carry.js. Written out here rather than only read off the route because
 * the page has to decide whether to DRAW each button before any modal is
 * opened, and the report payload is what it has at that moment.
 *
 *   carry  = clamp(carriedIn, min(0, pot), max(0, pot))
 *   period = pot - carry                 they always sum to the pot
 *
 * SIGNED, and that is the point. A pot below zero is a standing loss, and
 * settling against it realises the deficit rather than banking profit. Clamped
 * to zero instead — which is what this page used to do — the controls simply
 * never appeared on a losing book and the loss could not be settled at all.
 */
const settleLimits = (carriedIn, pot) => {
  const p = Number(pot) || 0;
  const carry = Math.min(Math.max(Number(carriedIn) || 0, Math.min(0, p)), Math.max(0, p));
  return { pot: p, carry, period: p - carry };
};
/** Is this limit worth offering a button for? Magnitude, so a loss counts. */
const hasLimit = (v) => Math.abs(Number(v) || 0) > 0.004;
const sumBy = (rows, key) => (rows || []).reduce((a, r) => a + (r[key] || 0), 0);

/**
 * Caption for a Settled P&L figure. Says plainly that nothing is settled yet
 * when nothing has been, rather than leaving a bare "+$0.00" that reads like a
 * missing number.
 *
 * The test is whether this account HAS a settled figure - not whether it made a
 * withdrawal itself. Those used to be the same thing, and are not any more: a
 * withdrawal now settles the pair's whole standing position and allocates it
 * across both accounts, so an account can be fully settled without ever having
 * withdrawn a cent. Keying off `settlements` (its own withdrawal count) captioned
 * CMS Prime's banked +$41.00 as "not yet settled" - the label flatly
 * contradicting the number directly above it - because Honor made the withdrawal
 * that settled it.
 *
 * `settlements` is still consulted so that an account holding exactly zero after
 * a real withdrawal still reads "unsettled +$0" rather than "not yet settled".
 */
const settledSub = (o, ccy) =>
  o && (Math.abs(o.settledPnl || 0) > 0.004 || o.settlements)
    ? // The settlement COUNT is deliberately not shown: with it this label needed
      // 139px in a 136px cell and truncated the money figure. `settlements` is on
      // the payload if it is ever wanted somewhere roomier.
      `unsettled ${signed(o.unsettledPnl, ccy, 0)}`
    : "not yet settled";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format a timestamp off the payload.
 *
 * Every time on this payload — deal open/close times, the server clock, the
 * inception date — is ALREADY in the report timezone (IST), expressed as a Unix
 * epoch. The MT5 library reports BROKER-local time rather than UTC, and the
 * poller converts that into one shared zone at the boundary; see THE TIMESTAMP
 * CONVENTION in portfolio-backend/mt5/report.js and mt5/tz.js.
 *
 * So it is formatted with the UTC getters and NO offset is applied. Adding
 * tzOffsetMinutes here would shift every timestamp on the page five and a half
 * hours into the future. 24-hour clock throughout — these are trade timestamps,
 * and am/pm on a 21:30 fill reads as an error.
 *
 * tzOffsetMinutes is still on the payload, but only to LABEL the zone.
 */
/**
 * Names the zone the timestamps above are already in.
 *
 * Prefers the label the backend sends, so the page and the report agree on one
 * word. Falls back to deriving it, which is what makes an older cached payload
 * (no tzLabel field) still render sensibly instead of blank.
 */
function tzLabel(minutes, label) {
  if (label) return label;
  const m = minutes || 0;
  if (m === 330) return "IST";
  const sign = m < 0 ? "-" : "+";
  const h = Math.floor(Math.abs(m) / 60);
  const mm = Math.abs(m) % 60;
  return `GMT${sign}${h}${mm ? ":" + String(mm).padStart(2, "0") : ""}`;
}

function serverTime(unix) {
  if (!unix) return { date: "—", time: "" };
  const d = new Date(unix * 1000);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const yy = String(d.getUTCFullYear()).slice(2);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return { date: `${dd} ${MONTHS[d.getUTCMonth()]} ${yy}`, time: `${hh}:${mi}:${ss}` };
}

const ago = (ms) => {
  if (ms == null) return "—";
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

/* ------------------------------ small pieces ------------------------------ */

function Sparkline({ values, positive, baseline, width = 90, height = 30 }) {
  // Fewer than two points is not a line — but return an EMPTY BOX OF THE SAME
  // SIZE rather than nothing. This sits in the "Account status" .ir-block-head,
  // which is 17px tall on its own and 30px with a sparkline in it. An account
  // with under two closed trades therefore gave its whole panel a 13px head
  // start on the panel beside it, so the two Live positions / Trade log tables
  // no longer began on the same line. Reserving the space keeps them level.
  if (!values || values.length < 2) {
    return <svg className="ir-spark" width={width} height={height} aria-hidden="true" />;
  }
  /* The baseline is folded into the extent so the reference line is always on
     screen. Without it, an account that never dipped below its opening balance
     would scale the curve off the top of the box and the reference would be
     invisible - which is the one comparison this graph exists to make. */
  const hasBase = Number.isFinite(baseline);
  const extent = hasBase ? values.concat([baseline]) : values;
  const min = Math.min(...extent);
  const max = Math.max(...extent);
  const span = max - min || 1;
  const y = (v) => height - 2 - ((v - min) / span) * (height - 4);
  const pts = values.map((v, i) => [(i / (values.length - 1)) * (width - 2) + 1, y(v)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
  const stroke = positive ? "var(--ir-up)" : "var(--ir-down)";
  return (
    <svg className="ir-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={`${line} L${pts[pts.length - 1][0]} ${height} L${pts[0][0]} ${height} Z`} fill={stroke} opacity="0.1" />
      {hasBase && (
        <line
          x1="1"
          x2={width - 1}
          y1={y(baseline)}
          y2={y(baseline)}
          stroke="var(--ir-muted)"
          strokeWidth="1"
          strokeDasharray="2 2"
          opacity="0.55"
        />
      )}
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill={stroke} stroke="var(--ir-surface)" strokeWidth="2" />
    </svg>
  );
}

function Delta({ value, dp = 2 }) {
  const Icon = (value || 0) >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`ir-delta ir-${dir(value)}`}>
      <Icon size={13} strokeWidth={2.5} aria-hidden="true" />
      {pctStr(value, dp)}
    </span>
  );
}

function DateTime({ unix }) {
  const { date, time } = serverTime(unix);
  return (
    <span className="ir-dt">
      <span className="ir-dt-d">{date}</span>
      <span className="ir-dt-t">{time}</span>
    </span>
  );
}

/**
 * `action` is a SIBLING of `sub`, not part of it.
 *
 * `sub` renders inside an <em>, which is where the caption belongs; a control in
 * there would be emphasised text wrapping a button. Kept separate so the caption
 * stays a caption and the tile can still offer something to do about it.
 */
function Figure({ label, value, cls, sub, action }) {
  return (
    <div className="ir-fig">
      <span className="ir-fig-label">{label}</span>
      <b className={`ir-fig-value ${cls || ""}`}>{value}</b>
      {sub && <em className="ir-fig-sub">{sub}</em>}
      {action}
    </div>
  );
}

/**
 * THE ALL-CONTAINERS VIEW.
 *
 * Deliberately the same three blocks, in the same order, as a single container's
 * page: the combined hero with its figure tiles, then Combined statistics, then
 * — where one container shows its two account panels — one card per container.
 * Somebody switching the selector to All should recognise the page they were
 * just on.
 *
 * NO TRADE LOG AND NO POSITIONS, by decision: those need a container selected.
 *
 * The chips under the hero are one per CONTAINER here, where a single
 * container's view puts one per account. Same furniture, one level up.
 *
 * `PeriodStats` is the page's own widget, unchanged — which is why the All
 * periods had to be given a `pct` server-side rather than the widget being
 * taught to live without one.
 */
function AllContainersView({ report, ccy, inception }) {
  const c = report.combined || {};
  const cards = report.cards || [];
  const excluded = report.excludedContainers || [];
  const history = c.settlementHistory || [];

  return (
    <>
      <section className="ir-combined" aria-labelledby="ir-all-t">
        <div className="ir-comb-hero">
          <span className="ir-eyebrow">
            <Layers size={13} aria-hidden="true" /> All containers &middot; {c.connectedCount}/
            {c.accountCount} live
          </span>
          <h1 id="ir-all-t" className="ir-hero-figure">
            {money(c.equity, ccy, 2)}
          </h1>
          <div className="ir-hero-deltas">
            <Delta value={c.returnPct} />
            <span className="ir-hero-abs">{signed(c.closedPnl, ccy, 2)} closed P&amp;L</span>
          </div>
          {/* One chip per container. An excluded one is styled as `past` — it is
              on the page but outside the headline above it, which is exactly the
              distinction that styling already carries for a retired account. */}
          <div className="ir-comb-chips">
            {cards.map((k, i) => (
              <span
                key={k.id}
                className={`ir-chip ${
                  k.includeInOverall === false ? "ir-chip-past" : `ir-chip-${i % 2}`
                }`}
              >
                <b>{k.name}</b>
                {money(k.equity, ccy, 0)}
                <em className={k.includeInOverall === false ? "ir-muted" : `ir-${dir(k.sessionPnl)}`}>
                  {k.includeInOverall === false ? "not counted" : signed(k.sessionPnl, ccy, 0)}
                </em>
              </span>
            ))}
          </div>
          <div className="ir-trust">
            <BadgeCheck size={14} aria-hidden="true" />
            Live read-only feed &middot; figures in {ccy}
          </div>
        </div>

        <div className="ir-comb-figs">
          <Figure
            label="Containers counted"
            value={`${c.includedCount} of ${c.containerCount}`}
            sub={c.includedCount === c.containerCount ? "all of them" : "some excluded"}
          />
          <Figure label="Opening capital" value={money(c.openingBalance, ccy)} sub="period start" />
          <Figure label="Balance" value={money(c.balance, ccy)} />
          <Figure label="Closed P&L" value={signed(c.closedPnl, ccy)} cls={`ir-${dir(c.closedPnl)}`} />
          <Figure label="Floating P&L" value={signed(c.floating, ccy)} cls={`ir-${dir(c.floating)}`} />
          <Figure
            label="Session P&L"
            value={signed(c.sessionPnl, ccy)}
            cls={`ir-${dir(c.sessionPnl)}`}
            sub="02:00 – 02:00"
          />
          <Figure
            label="Unsettled P&L"
            value={signed(c.unsettled, ccy)}
            cls={`ir-${dir(c.unsettled)}`}
            sub="not yet withdrawn"
          />
          <Figure
            label="Settled P&L"
            value={signed(c.settled, ccy)}
            cls={`ir-${dir(c.settled)}`}
            sub={history.length ? `${history.length} withdrawal${history.length === 1 ? "" : "s"} banked` : "not yet settled"}
          />
        </div>
      </section>

      {excluded.length > 0 && (
        <div className="ir-banner is-warn">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>
            On this page but <b>not</b> in the totals above:{" "}
            {excluded.map((x) => `${x.name}${x.mode === "test" ? " (test)" : ""}`).join(", ")}. A
            disabled container is not shown here at all.
          </span>
        </div>
      )}

      <section className="ir-card">
        <div className="ir-block-head">
          <h3>
            <Wallet size={15} aria-hidden="true" /> Combined statistics
          </h3>
          <span className="ir-block-note">
            across the {c.includedCount} counted container{c.includedCount === 1 ? "" : "s"} &middot;
            trading days run 02:00 to 02:00
          </span>
        </div>
        <PeriodStats periods={c.periods} ccy={ccy} size="md" inception={inception} />
      </section>

      {/* WHERE SETTLED AND UNSETTLED COME FROM. The single-container page has
          this reconciliation and All needs it too, or the combined settled figure
          is a number with nothing behind it. Each row names its container: a
          list of amounts nobody can tie to a book explains nothing.

          COLLAPSED BY DEFAULT, and the same native <details> the
          single-container page uses rather than a mechanism of its own — same
          element, same classes, same CSS, so the two sections open and close
          identically instead of merely looking alike. It answers a question most
          readers will not ask, and when a figure looks wrong it is the only
          thing that explains it. */}
      {history.length > 0 && (
        <details className="ir-settle">
          <summary>
            <span className="ir-settle-title">Settled &amp; unsettled — how these are made up</span>
            <span className="ir-settle-count">
              {history.length} withdrawal{history.length === 1 ? "" : "s"} across the counted
              container{c.includedCount === 1 ? "" : "s"}
            </span>
          </summary>

          <div className="ir-settle-body">
          <div className="ir-tbl-wrap">
            <table className="ir-alltbl">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Container</th>
                  <th scope="col">Account</th>
                  <th scope="col" className="ir-r">Withdrawn</th>
                  <th scope="col" className="ir-r">Settled</th>
                  <th scope="col" className="ir-r">Capital back</th>
                </tr>
              </thead>
              <tbody>
                {history
                  .slice()
                  .reverse()
                  .map((r) => (
                    <tr key={`${r.container}-${r.login}-${r.at}`}>
                      <td><DateTime unix={r.at} /></td>
                      <td>{r.container}</td>
                      <td>{r.label || `#${r.login}`}</td>
                      <td className="ir-r">{money(r.withdrawn, ccy)}</td>
                      <td className={`ir-r ir-${dir(r.settled)}`}>{signed(r.settled, ccy)}</td>
                      <td className="ir-r">{money(r.capitalReturned, ccy)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          </div>
        </details>
      )}

      <section className="ir-card">
        <div className="ir-block-head">
          <h3>
            <Layers size={15} aria-hidden="true" /> Each container
          </h3>
          <span className="ir-block-note">
            pick one from the selector above for its positions and trade log
          </span>
        </div>
        <div className="ir-ccards">
          {cards.map((k) => (
            <ContainerCard key={k.id} card={k} ccy={ccy} />
          ))}
        </div>
      </section>
    </>
  );
}

/**
 * ONE CONTAINER'S CARD.
 *
 * Used for a container the poller is not holding — its figures are real and
 * simply not current — and reused by the All view, which is one of these per
 * container.
 *
 * Built from THIS page's own parts: `Figure`, `Delta`, `money`, `signed` and the
 * `ir-` classes. Not a design of its own. A card with its own prefix and its own
 * palette produces a page that plainly does not belong to the report it sits
 * inside: right figures, foreign furniture.
 *
 * WHAT EACH LINE IS FOR:
 *   per account   balance and its return, so the two legs can be compared
 *   Total         the container's equity and the blended return
 *   Opening       the capital the period began with — without it a percentage
 *                 has no base and "227,506 (+13.75%)" cannot be checked
 *   Floating      open-position P&L, kept apart from realised
 *   Lots          PER LEG. The accounts are hedged, so unequal lots is a FAULT —
 *                 a leg that did not fill — and "4 each" hides the one anomaly
 *                 worth seeing. `legsMatch === false` says so; null means there
 *                 was nothing to compare, which must not shout.
 *   Session       the trading day's realised P&L, 02:00 to 02:00
 *   Settled       how much is banked versus still standing
 */
function ContainerCard({ card, ccy }) {
  const lots = card.lots || [];
  const legsDiffer = card.legsMatch === false;

  return (
    <article className={`ir-ccard${card.live ? "" : " is-stale"}`}>
      <header className="ir-ccard-head">
        <h3 className="ir-ccard-name">{card.name}</h3>
        <div className="ir-ccard-tags">
          {card.isDefault && <span className="ir-ctag is-default">default</span>}
          {card.mode === "test" && <span className="ir-ctag is-test">test</span>}
          {card.includeInOverall === false && (
            <span className="ir-ctag is-out" title="On this page, but not counted in the combined totals">
              not in totals
            </span>
          )}
          {card.publicVisible === false && (
            <span className="ir-ctag is-private" title="Hidden from anyone not signed in as a developer">
              private
            </span>
          )}
        </div>
      </header>

      <div className="ir-ccard-hero">
        <b className="ir-ccard-figure">{money(card.equity, ccy, 2)}</b>
        {card.openingBalance > 0 && <Delta value={card.returnPct} />}
      </div>

      <div className="ir-ccard-rows">
        {card.accounts.map((a, i) => (
          <div className="ir-ccard-row" key={a.login}>
            <span className={`ir-ccard-key ir-chip-${i}`} aria-hidden="true" />
            <span className="ir-ccard-row-l">{a.label || `#${a.login}`}</span>
            <span className="ir-ccard-row-v">{money(a.balance, ccy, 2)}</span>
            <span className={`ir-ccard-row-p ir-${dir(a.returnPct)}`}>
              {a.openingBalance > 0
                ? `${a.returnPct >= 0 ? "+" : "−"}${Math.abs(a.returnPct).toFixed(2)}%`
                : "—"}
            </span>
          </div>
        ))}
        {/* RELEASED ACCOUNTS, NAMED RATHER THAN OMITTED.
            A released account is still in the pair and still holds real money;
            it is simply not being recorded here, because another container is.
            Leaving it out made the card read as loss - a container showed no
            accounts and a zero balance beside a settled figure of six figures.

            Its balance is shown but NOT added to the card's totals, and the row
            says so: whichever container records it now is already counting it,
            and counting it twice is the capital double-count the whole
            release mechanism exists to avoid. */}
        {(card.released || []).map((r) => (
          <div className="ir-ccard-row is-released" key={`rel-${r.login}`}>
            <span className="ir-ccard-key" aria-hidden="true" />
            <span className="ir-ccard-row-l">
              {r.label || `#${r.login}`}
              <em className="ir-ccard-rel">released</em>
            </span>
            <span className="ir-ccard-row-v">{money(r.lastBalance, ccy, 2)}</span>
            <span className="ir-ccard-row-p">not counted</span>
          </div>
        ))}

        {card.accounts.length === 0 &&
          (card.namedCount > 0 ? (
            <p className="ir-ccard-empty">
              This container is not recording either of its accounts — they have been released to
              another container. Its history below is unchanged, and its money is counted wherever it
              is being recorded now. Re-apply its pair to start recording again.
            </p>
          ) : (
            <p className="ir-ccard-empty">
              No accounts are assigned to this container yet. Add them on the developer accounts
              page, then set its period.
            </p>
          ))}
      </div>

      <div className="ir-ccard-figs">
        <Figure label="History from" value={card.startDate || "—"} sub="container start" />
        <Figure label="Opening capital" value={money(card.openingBalance, ccy)} sub="period start" />
        <Figure label="Floating P&L" value={signed(card.floating, ccy)} cls={`ir-${dir(card.floating)}`} />
        <Figure
          label="Lots"
          value={lots.length ? lots.map((x) => (x == null ? "—" : Number(x).toFixed(2))).join(" / ") : "—"}
          cls={legsDiffer ? "ir-down" : ""}
          sub={legsDiffer ? "legs differ" : "per leg"}
        />
        <Figure
          label="Session P&L"
          value={signed(card.sessionPnl, ccy)}
          cls={`ir-${dir(card.sessionPnl)}`}
          sub="02:00 – 02:00"
        />
        <Figure
          label="Unsettled P&L"
          value={signed(card.unsettled, ccy)}
          cls={`ir-${dir(card.unsettled)}`}
          sub="not yet withdrawn"
        />
        <Figure
          label="Settled P&L"
          value={signed(card.settled, ccy)}
          cls={`ir-${dir(card.settled)}`}
          sub="banked"
        />
      </div>

      <footer className="ir-ccard-foot">
        <span
          className={`ir-ccard-dot is-${
            card.accountCount === 0
              ? "none"
              : card.live && card.connectedCount === card.accountCount
              ? "on"
              : card.connectedCount
              ? "part"
              : "off"
          }`}
          aria-hidden="true"
        />
        <span>
          {card.accountCount === 0
            ? "no accounts"
            : card.live
            ? `${card.connectedCount}/${card.accountCount} connected`
            : "nothing polling it — figures as of the last poll"}
        </span>
        {card.asOf && <span className="ir-ccard-at">{new Date(card.asOf).toLocaleTimeString()}</span>}
      </footer>
    </article>
  );
}

function PeriodStats({ periods, ccy, size = "md", inception = 0 }) {
  return (
    <div className={`ir-periods is-${size}`}>
      {PERIOD_KEYS.map(({ key, label }) => {
        const p = (periods && periods[key]) || { pnl: 0, trades: 0, pct: 0 };
        return (
          <div key={key} className={`ir-period ir-period-${dir(p.pnl)}`}>
            <span className="ir-period-label">{label}</span>
            <span className={`ir-period-value ir-${dir(p.pnl)}`}>{signed(p.pnl, ccy, 2)}</span>
            <span className="ir-period-foot">
              <Delta value={p.pct} />
              <span className="ir-period-trades">
                {p.trades} trades
                {/* When the window was cut short by the inception date, say so —
                    otherwise "This year: $0.00" reads as a bug. No offset is
                    applied: the bucket start is report-zone midnight already
                    expressed as an epoch, so the UTC getters print the right
                    day. Adding tzOffsetMinutes here would show the day after. */}
                {/* p.since, NOT inception. The bucket carries its own window start, and
                    for month and year that is MT5_START_DATE. Printing inception here
                    labelled a figure covering 284 trades from 10 Aug as "since 24 Aug",
                    contradicting the header two lines up. */}
                {p.clamped && p.since ? ` · since ${serverTime(p.since).date}` : ""}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * ONE column budget shared by the Live positions and Trade log tables.
 *
 * Both tables have nine columns, and they sit directly above one another in the
 * same panel, so any difference in the shares puts their headings and values on
 * different vertical lines. They used to differ in the last three columns
 * (14/11/19 vs 13.5/13.5/17), which is exactly why "SWAP"/"CLOSED" and the two
 * "P&L" headings did not line up.
 *
 * Column 5 holds a price in both. Column 7 holds a MONEY value (Swap) in one
 * table and a DATE+TIME (Closed) in the other, so it is sized for the wider of
 * the two. Shares are verified by measurement — see .ir-tbl min-width in the CSS.
 *
 *   1 #  2 Symbol  3 Side  4 Lots  5 Open  6 Current/Close
 *   7 Opened  8 Swap/Closed   9 P&L
 *
 * Column 1 is a SERIAL NUMBER down the rows, so the two panels can be read
 * across: row 4 on the left is the same trade as row 4 on the right, because both
 * accounts trade the pair together and both tables are sorted the same way.
 *
 * It counts the ROWS SHOWN, not the trades in the period. The log arrives newest
 * first and capped at 30 (report.js), so a period-wide index is not derivable
 * here, and one that looked like it was would be wrong the moment the window slid.
 *
 * It is on BOTH tables, not just the log. The columns have to stay identical
 * between the two or their headings sit on different vertical lines - the whole
 * point of this shared budget - so a ninth column in one and not the other would
 * reintroduce exactly the misalignment described below.
 *
 * Commission is wanted for closed trades but not for open positions, so it is
 * NOT a column: a 10th column would have to sit empty in the positions table,
 * which reads as a gap between Swap and P&L. Instead the trade log stacks it
 * under each P&L value (see .ir-cell-sub), which keeps both tables at eight
 * identical columns — the only way their headings and values share one vertical
 * line — and costs no width at all.
 */
const TABLE_COLS = ["5%", "13.3%", "8.55%", "8.55%", "13.3%", "13.3%", "10.9%", "11.9%", "15.2%"];
const TABLE_COL_COUNT = TABLE_COLS.length;

const ColGroup = () => (
  <colgroup>
    {TABLE_COLS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

/** Blank rows so both panels' Total rows land on the same line. */
function FillerRows({ count, cols }) {
  if (count <= 0) return null;
  return Array.from({ length: count }, (_, i) => (
    <tr className="ir-filler" key={`f${i}`} aria-hidden="true">
      {/* Empty on a filler row: numbering a row that is not there would
          make the two panels' visible counts disagree with their logs. */}
      <td className="ir-idx" />
      <td>
        <span className="ir-sym">&nbsp;</span>
        <span className="ir-ticket">&nbsp;</span>
      </td>
      <td colSpan={cols - 2} />
    </tr>
  ));
}

/* ============================================================================
   ACCOUNT PANEL
   ========================================================================== */

function AccountPanel({ acc, index, maxRows, inception = 0, active = true }) {
  const ccy = acc.currency || "USD";
  const tz = acc.tzOffsetMinutes || 0;
  // Same zone on every panel now, so the two brokers can no longer be labelled
  // differently while showing the same trade.
  const tzName = tzLabel(tz, acc.tzLabel);

  // Trajectory from the cumulative net P&L of recent closed trades — real data,
  // no separate equity-history store needed.
  const trend = useMemo(() => {
    /* The BALANCE curve over the closed trades on record, rather than a
       P&L-from-zero line, so it can be read against the capital the account
       started with.

       Anchored from the END: the last point is the balance now, and the first is
       that balance less everything the plotted trades made. Anchoring at
       openingBalance instead would only line up while the log holds the whole
       period - it is capped at 30 rows - and would drift silently once a period
       ran longer than that. */
    const rows = (acc.log || []).slice().reverse(); // oldest first
    if (rows.length < 2) return null;
    const total = rows.reduce((t, r) => t + (r.pnl || 0), 0);
    let run = (acc.balance || 0) - total;
    const series = [run];
    for (const r of rows) series.push((run += r.pnl || 0));
    return series;
  }, [acc.log, acc.balance]);

  const meta = [
    acc.platform,
    `#${acc.login}`,
    acc.company,
    acc.leverage ? `1:${acc.leverage}` : null,
    ccy,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      id={`${acc.id}-panel`}
      className={`ir-account ir-account-${index}${active ? " is-active" : ""}`}
      aria-labelledby={`${acc.id}-t`}
    >
      <header className="ir-acc-head">
        <div className="ir-acc-id">
          <span className="ir-acc-badge" aria-hidden="true">
            {(acc.label || "??").slice(0, 2).toUpperCase()}
          </span>
          <div>
            <h2 id={`${acc.id}-t`}>{acc.label}</h2>
            <p>{meta}</p>
          </div>
        </div>
        <div className="ir-acc-equity">
          <span className="ir-acc-equity-label">Equity</span>
          <b>{money(acc.equity, ccy, 2)}</b>
          {acc.connected ? (
            <Delta value={acc.returnPct} />
          ) : (
            <span className="ir-delta ir-down">
              <WifiOff size={13} aria-hidden="true" /> offline
            </span>
          )}
        </div>
      </header>

      {!acc.connected && (
        <div className="ir-banner is-warn">
          <WifiOff size={15} aria-hidden="true" />
          <span>
            Not connected{acc.error ? ` — ${acc.error}` : ""}. Reconnecting automatically; figures below
            are from the last successful read.
          </span>
        </div>
      )}

      {/* ------------------------------ status ------------------------------ */}
      <div className="ir-block">
        <div className="ir-block-head">
          <h3>Account status</h3>
          {/* Always rendered — Sparkline reserves its own height when there is
              nothing to plot, so both panels' heads stay the same size. */}
          <Sparkline
            values={trend}
            baseline={acc.openingBalance}
            positive={!!trend && trend[trend.length - 1] >= (acc.openingBalance || 0)}
          />
        </div>
        <div className="ir-figgrid">
          <Figure label="Balance" value={money(acc.balance, ccy)} />
          <Figure label="Equity" value={money(acc.equity, ccy)} />
          <Figure
            label="Floating P&L"
            value={signed(acc.floating, ccy)}
            cls={`ir-${dir(acc.floating)}`}
            sub={`${(acc.positions || []).length} open`}
          />
          <Figure
            label="Closed P&L"
            value={signed(acc.closedPnl, ccy)}
            cls={`ir-${dir(acc.closedPnl)}`}
            sub="net of costs"
          />
          {/* Closed P&L above is already net of these. Shown so the deduction is
              visible rather than silently baked into the headline figure. */}
          <Figure
            label="Costs"
            value={signed(acc.costs, ccy)}
            cls={`ir-${dir(acc.costs)}`}
            /* Swap only when there is any — naming both always overflowed this
               one-line label even on a wide screen. */
            sub={
              acc.swap
                ? `comm ${signed(acc.commission, ccy)} · swap ${signed(acc.swap, ccy)}`
                : `comm ${signed(acc.commission, ccy)}`
            }
          />
          <Figure label="Deposits" value={money(acc.deposits, ccy)} />
          <Figure label="Withdrawals" value={money(acc.withdrawals, ccy)} cls="ir-down" />
          <Figure label="Net deposits" value={money(acc.netDeposits, ccy)} />
          {/* The base every percentage on this panel is measured against. Shown
              so "+2.39%" is checkable rather than taken on trust. */}
          <Figure
            label="Opening balance"
            value={money(acc.openingBalance, ccy)}
            sub="period start"
          />
          <Figure
            label="Total P&L"
            value={signed(acc.totalPnl, ccy)}
            cls={`ir-${dir(acc.totalPnl)}`}
            sub={`${pctStr(acc.returnPct, 2)} on ${money(acc.openingBalance, ccy, 0)}`}
          />
          <Figure label="Margin used" value={money(acc.margin, ccy)} />
          <Figure label="Free margin" value={money(acc.freeMargin, ccy)} />
          <Figure label="Margin level" value={plain(acc.marginLevel, 0)} cls="ir-up" />
          <Figure
            label="Open lots"
            value={(acc.lots || 0).toFixed(2)}
            sub={`${serverTime(acc.serverTime).time} ${tzName}`}
          />
          {/* LAST card, deliberately. Profit banked by withdrawing it — the P&L
              accrued at each withdrawal point, accumulated. Not the cash taken
              out; that is "Withdrawals" above. */}
          <Figure
            label="Settled P&L"
            value={signed(acc.settledPnl, ccy)}
            cls={`ir-${dir(acc.settledPnl)}`}
            sub={settledSub(acc, ccy)}
          />
        </div>
      </div>

      {/* --------------------------- live positions -------------------------- */}
      <div className="ir-block">
        <div className="ir-block-head">
          <h3>Live positions</h3>
          <span className="ir-block-note">
            {(acc.positions || []).length} open · {(acc.lots || 0).toFixed(2)} lots
          </span>
        </div>

        {/* The bare message only when NEITHER account has positions — then both
            panels render it and stay level. If the panel beside this one has
            rows, render the table skeleton instead so the two headers, row
            areas and Total rows still line up across the report. */}
        {(acc.positions || []).length === 0 && maxRows.positions === 0 ? (
          <p className="ir-empty">No open positions.</p>
        ) : (
          <>
            <div className="ir-tbl-wrap">
              <table className="ir-tbl ir-tbl-pos">
                <caption className="ir-sr">Open positions for {acc.label}.</caption>
                <ColGroup />
                <thead>
                  <tr>
                    <th scope="col" className="ir-idx">#</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="ir-r">Lots</th>
                    <th scope="col" className="ir-r">Open</th>
                    <th scope="col" className="ir-r">Current</th>
                    <th scope="col" className="ir-r">Opened</th>
                    <th scope="col" className="ir-r">Swap</th>
                    <th scope="col" className="ir-r">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {acc.positions.map((p, i) => (
                    <tr key={p.ticket}>
                      <td className="ir-idx">{i + 1}</td>
                      <td>
                        <span className="ir-sym">{p.symbol}</span>
                        <span className="ir-ticket">#{p.ticket}</span>
                      </td>
                      <td>
                        <span className={`ir-pill ir-pill-${(p.type || "").toLowerCase()}`}>{p.type}</span>
                      </td>
                      <td className="ir-r ir-num">{p.lots.toFixed(2)}</td>
                      <td className="ir-r ir-num">{p.openPrice.toFixed(p.digits)}</td>
                      <td className="ir-r ir-num ir-bold">{p.currentPrice.toFixed(p.digits)}</td>
                      <td className="ir-r">
                        <DateTime unix={p.openTime} />
                      </td>
                      <td className={`ir-r ir-num ir-xs ir-${dir(p.swap)}`}>{signed(p.swap, ccy)}</td>
                      <td className={`ir-r ir-num ir-bold ir-${dir(p.net)}`}>{signed(p.net, ccy)}</td>
                    </tr>
                  ))}
                  {acc.positions.length === 0 && (
                    <tr className="ir-none">
                      <td colSpan={TABLE_COL_COUNT}>No open positions.</td>
                    </tr>
                  )}
                  {/* The message row above already occupies one row's height. */}
                  <FillerRows
                    count={maxRows.positions - Math.max(acc.positions.length, 1)}
                    cols={TABLE_COL_COUNT}
                  />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Total</td>
                    <td className="ir-r ir-num">{(acc.lots || 0).toFixed(2)}</td>
                    <td />
                    <td />
                    <td />
                    {/* Swap carried by the open positions. */}
                    <td className={`ir-r ir-num ir-xs ir-${dir(sumBy(acc.positions, "swap"))}`}>
                      {signed(sumBy(acc.positions, "swap"), ccy)}
                    </td>
                    <td className={`ir-r ir-num ir-${dir(acc.floating)}`}>{signed(acc.floating, ccy)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <ul className="ir-rows">
              {/* The table above is hidden in the narrow card view, so the
                  message has to exist here too or the block reads as blank. */}
              {acc.positions.length === 0 && <li className="ir-row-none">No open positions.</li>}
              {acc.positions.map((p, i) => (
                <li key={p.ticket}>
                  <span className="ir-row-top">
                    <span className="ir-row-idx">{i + 1}</span>
                    <span className="ir-sym">{p.symbol}</span>
                    <span className={`ir-pill ir-pill-${(p.type || "").toLowerCase()}`}>{p.type}</span>
                    <span className="ir-num ir-xs ir-muted">{p.lots.toFixed(2)} lots</span>
                    <b className={`ir-num ir-row-pnl ir-${dir(p.net)}`}>{signed(p.net, ccy)}</b>
                  </span>
                  <span className="ir-row-mid ir-num">
                    {p.openPrice.toFixed(p.digits)} <i aria-hidden="true">→</i>{" "}
                    <b>{p.currentPrice.toFixed(p.digits)}</b>
                  </span>
                  <span className="ir-row-foot">
                    <em>
                      Opened {serverTime(p.openTime).date} {serverTime(p.openTime).time}
                    </em>
                    <em className={`ir-${dir(p.swap)}`}>swap {signed(p.swap, ccy)}</em>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* ------------------------------ trade log --------------------------- */}
      <div className="ir-block ir-block-log">
        <div className="ir-block-head">
          <h3>Trade log</h3>
          <span className="ir-block-note">
            last {(acc.log || []).length} closed · P&amp;L net of commission &amp; swap
          </span>
        </div>

        {/* As with positions: only drop to a bare message when NEITHER account
            has closed trades, so the two logs stay aligned. */}
        {(acc.log || []).length === 0 && maxRows.log === 0 ? (
          <p className="ir-empty">No closed trades in the reporting window.</p>
        ) : (
          <>
            <div className="ir-tbl-wrap ir-log-scroll">
              <table className="ir-tbl ir-tbl-log">
                <caption className="ir-sr">Closed trades for {acc.label}.</caption>
                <ColGroup />
                <thead>
                  <tr>
                    <th scope="col" className="ir-idx">#</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="ir-r">Lots</th>
                    <th scope="col" className="ir-r">Open</th>
                    <th scope="col" className="ir-r">Close</th>
                    <th scope="col" className="ir-r">Opened</th>
                    <th scope="col" className="ir-r">Closed</th>
                    <th scope="col" className="ir-r">P&amp;L / comm.</th>
                  </tr>
                </thead>
                <tbody>
                  {acc.log.map((t, i) => (
                    <tr key={t.ticket}>
                      <td className="ir-idx">{i + 1}</td>
                      <td>
                        <span className="ir-sym">{t.symbol}</span>
                        <span className="ir-ticket">#{t.ticket}</span>
                      </td>
                      <td>
                        <span className={`ir-pill ir-pill-${(t.type || "").toLowerCase()}`}>{t.type}</span>
                      </td>
                      <td className="ir-r ir-num">{t.lots.toFixed(2)}</td>
                      <td className="ir-r ir-num">{t.openPrice.toFixed(t.digits)}</td>
                      <td className="ir-r ir-num">{t.closePrice.toFixed(t.digits)}</td>
                      <td className="ir-r">
                        <DateTime unix={t.openTime} />
                      </td>
                      <td className="ir-r">
                        <DateTime unix={t.closeTime} />
                      </td>
                      {/* Commission stacked under the P&L it was deducted from,
                          rather than taking a column the positions table would
                          have to leave empty. Always rendered, including at
                          zero, so every row is the same height and the two
                          panels' Total rows stay on one line. */}
                      <td className="ir-r">
                        <span className={`ir-num ir-bold ir-${dir(t.pnl)}`}>{signed(t.pnl, ccy)}</span>
                        <span className={`ir-cell-sub ir-num ir-${dir(t.commission)}`}>
                          comm {signed(t.commission, ccy)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {acc.log.length === 0 && (
                    <tr className="ir-none">
                      <td colSpan={TABLE_COL_COUNT}>No closed trades in the reporting window.</td>
                    </tr>
                  )}
                  {/* The message row above already occupies one row's height. */}
                  <FillerRows count={maxRows.log - Math.max(acc.log.length, 1)} cols={TABLE_COL_COUNT} />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Shown trades</td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td className="ir-r">
                      <span className={`ir-num ir-${dir(acc.log.reduce((a, x) => a + x.pnl, 0))}`}>
                        {signed(acc.log.reduce((a, x) => a + x.pnl, 0), ccy)}
                      </span>
                      <span className={`ir-cell-sub ir-num ir-${dir(sumBy(acc.log, "commission"))}`}>
                        comm {signed(sumBy(acc.log, "commission"), ccy)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <ul className="ir-rows ir-rows-log">
              {acc.log.length === 0 && (
                <li className="ir-row-none">No closed trades in the reporting window.</li>
              )}
              {acc.log.map((t, i) => (
                <li key={t.ticket}>
                  <span className="ir-row-top">
                    <span className="ir-row-idx">{i + 1}</span>
                    <span className="ir-sym">{t.symbol}</span>
                    <span className={`ir-pill ir-pill-${(t.type || "").toLowerCase()}`}>{t.type}</span>
                    <span className="ir-num ir-xs ir-muted">{t.lots.toFixed(2)} lots</span>
                    <b className={`ir-num ir-row-pnl ir-${dir(t.pnl)}`}>{signed(t.pnl, ccy)}</b>
                  </span>
                  <span className="ir-row-mid ir-num">
                    {t.openPrice.toFixed(t.digits)} <i aria-hidden="true">→</i> {t.closePrice.toFixed(t.digits)}
                  </span>
                  <span className="ir-row-foot">
                    <em>
                      In {serverTime(t.openTime).date} {serverTime(t.openTime).time}
                    </em>
                    <em>
                      Out {serverTime(t.closeTime).date} {serverTime(t.closeTime).time}
                    </em>
                    <em className={`ir-${dir(t.commission)}`}>comm {signed(t.commission, ccy)}</em>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* ---------------------------- period stats -------------------------- */}
      <div className="ir-block">
        <div className="ir-block-head">
          <h3>{acc.label} statistics</h3>
        </div>
        <PeriodStats periods={acc.periods} ccy={ccy} size="sm" inception={acc.inception || inception} />
      </div>
    </section>
  );
}

/* ============================================================================
   PAGE
   ========================================================================== */

/**
 * The report page.
 *
 * `endpoint` chooses which view of the snapshot to render. Both views share this
 * component verbatim so the investment-portfolio page cannot fall behind the real
 * one — a layout or column fix lands on both at once. The rendered output is
 * identical; the two differ only in which endpoint they read.
 *
 * The payload still carries `normalised: true` (see mt5/normalise.js) even though
 * nothing on the page prints it, so anything consuming the API can still tell the
 * two views apart.
 */
/**
 * Is the person looking at this page a signed-in developer?
 *
 * This page is PUBLIC — most readers have no session at all — so this is only
 * about whether to draw the dismiss button. It is not the authorisation: the
 * endpoint behind the button is guarded with `protect + isDeveloper`, and a
 * reader who forged this check would get a 403 rather than a cleared banner.
 *
 * Read on every render rather than cached: signing out in another tab should not
 * leave a button that no longer works.
 */
function isDeveloperViewer() {
  try {
    if (!localStorage.getItem("token")) return false;
    const u = JSON.parse(localStorage.getItem("user"));
    return Boolean(u && u.role === "developer");
  } catch {
    /* storage blocked, or half-cleared — treat as a plain visitor */
    return false;
  }
}

/**
 * `allContainers` — LIST EVERY BOOK IN THE DROPDOWN, including ones marked not
 * public and ones disabled.
 *
 * /all/investment/reports passes this. It is the ONLY difference from
 * /investor-report: same layout, same figures, same everything else, one longer
 * list. It reads a separate endpoint rather than sending a flag to the ordinary
 * one, so the public report keeps filtering by who is asking.
 *
 * NOT expressed through `endpoint`, even though it changes which endpoint is
 * read: `endpoint` also means "this page has no container selector" (see
 * `selectable` below), and this page is nothing BUT its selector.
 */
export default function InvestorReport({ endpoint, allContainers = false }) {
  /* The all-containers feed, whose /stream and /containers mirror the ordinary
     ones. Undefined falls through to the hook's own default. */
  const feed = allContainers ? "/api/investor-report/all-containers" : endpoint;
  /**
   * WHICH CONTAINER THIS VIEWER IS LOOKING AT.
   *
   * Held in the URL rather than in component state, for three reasons that all
   * matter on a PUBLIC page: the selection is PER VIEWER, so one person
   * switching moves nobody else's view; a particular container becomes a link
   * somebody can be sent; and a reload comes back to the same container instead
   * of jumping to the default.
   *
   * /investment-portfolio and -v1 pass an `endpoint` and get NO selector, by
   * decision — they read the default container. So the parameter is honoured
   * only on the main report; otherwise those pages would silently take a
   * container from a URL they give you no way to change.
   */
  const selectable = !endpoint;
  const [container, setContainer] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("container") || "";
  });

  const chooseContainer = useCallback((next) => {
    setContainer(next);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (next) url.searchParams.set("container", next);
    else url.searchParams.delete("container");
    /* replaceState, not pushState: flicking through containers should not fill
       the Back button with steps nobody wants to walk back through. */
    window.history.replaceState({}, "", url);
  }, []);

  const { report, poller, error, live, polling, checkedAt, refresh } = useInvestorReport(feed, {
    container: selectable ? container : "",
  });

  /* The server decides which containers this viewer may see and which one is
     selected, and says so on every response — so the selector renders what it
     was told rather than deciding for itself. */
  const containerList = (report && report.containers) || [];
  const selection = (report && report.selection) || null;
  const selectedContainer = selection
    ? selection.mode === "all"
      ? "all"
      : selection.containerId || ""
    : container;
  const showingAll = Boolean(selection && selection.mode === "all");
  const canDismiss = isDeveloperViewer();

  /* Keys cleared in this tab, so the banner goes at once rather than on the next
     snapshot. The server is what actually removes it for everyone; this only
     stops the click feeling like it did nothing. */
  const [justCleared, setJustCleared] = useState([]);
  const [dismissError, setDismissError] = useState("");

  const dismissNotice = async (key) => {
    setDismissError("");
    setJustCleared((prev) => [...prev, key]);
    try {
      const res = await fetch("/api/dev/notices/dismiss", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        /* THE CONTAINER, because a dismissal now belongs to one. A notice key is
           built from the event, so two containers watching the same account
           produce the same key - without this the request falls back to the
           default container and would clear the banner on a book the reader is
           not even looking at. */
        body: JSON.stringify({ key, container: selectedContainer === "all" ? "" : selectedContainer }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `failed (${res.status})`);
      }
    } catch (err) {
      /* Put it back. A banner that vanished locally while the server still sends
         it to everyone else is the worst outcome — it would read as cleared. */
      setJustCleared((prev) => prev.filter((k) => k !== key));
      setDismissError(`Could not clear that notice: ${err.message}`);
    }
  };
  /* ------------------------------------------------------------------------
     MANUAL SETTLEMENT of the carried-in profit.

     `settle` holds the server's answer - the carry, the ceiling, and what has
     already been banked by hand - fetched when the modal opens rather than read
     off the report payload. The report is a poll behind and the ceiling has to
     be the figure the POST will actually check against, or the modal would offer
     a maximum that is then refused.
     ------------------------------------------------------------------------ */
  const [settle, setSettle] = useState(null); // { carriedIn, carryCeiling, periodCeiling, ... }
  /**
   * WHICH POT THIS MODAL IS SETTLING — "carry" or "period".
   *
   * One modal serving two buttons rather than two modals: the shape is
   * identical and the only differences are the ceiling, the title and one
   * sentence. Two copies would be two places to fix the next time either
   * changes, and the figures are the part nobody would notice drifting.
   */
  const [settleScope, setSettleScope] = useState("carry");
  const [settleAmount, setSettleAmount] = useState("");
  const [settleNote, setSettleNote] = useState("");
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleError, setSettleError] = useState("");
  const [settleDone, setSettleDone] = useState("");
  /**
   * WHICH OLD SETTLEMENT IS ARMED FOR UNDO.
   *
   * Undoing one made minutes ago is an undo. Undoing one made three weeks ago is
   * a REWRITE: settled falls, the carry rises, the reconciliation recomputes,
   * and a figure that may already have been reported changes with no trace on
   * the page. Both are allowed - nothing should lose recoverability - but the
   * second asks twice and says what it will do.
   *
   * KEYED ON AGE, NOT ON THE CALENDAR DAY. A settlement made at 23:50 would
   * otherwise need confirming at 00:05, and one made at 00:10 would stay
   * one-click until 23:59; the boundary would land in the middle of a working
   * evening rather than between one and the next.
   */
  const [armedUndo, setArmedUndo] = useState("");

  const settleUrl =
    selectedContainer && selectedContainer !== "all"
      ? `/api/dev/containers/${selectedContainer}/manual-settlements`
      : "";

  const settleFetch = async (method, path, body) => {
    const res = await fetch(path, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.message || out.error || `failed (${res.status})`);
    return out;
  };

  /* The limit for whichever pot is in play. The server sends both on every
     read, so switching button never needs a second request.

     IT IS SIGNED. Negative means the book is under water and this settlement
     realises that much of the deficit, so every comparison against it below
     works on magnitude and direction rather than assuming profit. */
  const ceilOf = (data, scope) =>
    !data ? 0 : scope === "period" ? data.periodCeiling || 0 : data.carryCeiling || 0;
  /**
   * IS THIS TYPED AMOUNT A VALID SETTLEMENT OF THIS LIMIT?
   *
   * One place, used by the warning, the running note and the submit button, so
   * the three can never disagree about what is acceptable — the failure that
   * version of this would be hardest to notice is a button that stays disabled
   * with nothing on screen explaining why.
   *
   *   empty      nothing typed yet
   *   exhausted  the POT is empty, so there is nothing to type. Distinguished
   *              from `zero` because the field is reset to the limit after a
   *              settlement: settling the lot leaves "0" sitting in the box,
   *              and "zero settles nothing" under a success message would read
   *              as the settlement having failed.
   *   zero       settles nothing, while the pot still holds something
   *   wrongWay   the sign does not match the book — profit asked of a losing
   *              book, or a loss of a winning one. Both replays would clamp it
   *              to nothing, so it is refused rather than silently dropped.
   *   tooMuch    beyond the limit, compared on MAGNITUDE with the same cent of
   *              tolerance the route allows
   */
  const checkAmount = (text, limit) => {
    const lim = Number(limit) || 0;
    if (Math.abs(lim) <= 0.004) return { empty: false, ok: false, exhausted: true };
    if (text === "") return { empty: true, ok: false };
    const n = Number(text);
    if (!Number.isFinite(n)) return { empty: false, ok: false, nan: true };
    if (Math.abs(n) <= 0.004) return { empty: false, ok: false, zero: true };
    if (Math.sign(n) !== Math.sign(lim)) return { empty: false, ok: false, wrongWay: true, n };
    if (Math.abs(n) > Math.abs(lim) + 0.004) return { empty: false, ok: false, tooMuch: true, n };
    return { empty: false, ok: true, n };
  };

  const openSettle = async (scope = "carry") => {
    if (!settleUrl) return;
    setSettleScope(scope);
    setSettleError("");
    setSettleDone("");
    /* Cleared on open AND on close. An armed row left behind would come back
       armed next time the modal is opened, so the first click a developer makes
       on a fresh modal could be a Confirm they never armed. */
    setArmedUndo("");
    setSettleBusy(true);
    try {
      const data = await settleFetch("GET", settleUrl);
      setSettle(data);
      /* DEFAULTED TO THE WHOLE OF THIS POT, as asked. Held as text, not a
         number, so the field can be cleared while typing - a numeric state
         would snap an empty box back to 0 on the first keystroke. */
      setSettleAmount(String(ceilOf(data, scope)));
      setSettleNote("");
    } catch (err) {
      setSettleError(err.message);
      setSettle({ failed: true });
    } finally {
      setSettleBusy(false);
    }
  };

  const reloadSettle = async () => {
    try {
      const data = await settleFetch("GET", settleUrl);
      setSettle(data);
      setSettleAmount(String(ceilOf(data, settleScope)));
    } catch (err) {
      setSettleError(err.message);
    }
  };

  const submitSettle = async () => {
    setSettleError("");
    setSettleDone("");
    setSettleBusy(true);
    try {
      const out = await settleFetch("POST", settleUrl, {
        amount: Number(settleAmount),
        note: settleNote,
        /* NAMED, not inferred from the amount. The route decides the ceiling
           and the effective instant from this, and the two pots are the whole
           point of the feature — leaving it out would silently settle the
           carry when the reader pressed the period button. */
        scope: settleScope,
      });
      setSettleDone(out.note || "Settled.");
      await reloadSettle();
      /* The report is rebuilt server-side by the route; this pulls the new
         figures now rather than waiting for the next poll to arrive. */
      refresh();
    } catch (err) {
      setSettleError(err.message);
    } finally {
      setSettleBusy(false);
    }
  };

  const removeSettle = async (id) => {
    setSettleError("");
    setSettleDone("");
    setSettleBusy(true);
    try {
      const out = await settleFetch("DELETE", `${settleUrl}/${id}`);
      setSettleDone(out.note || "Removed.");
      await reloadSettle();
      refresh();
    } catch (err) {
      setSettleError(err.message);
    } finally {
      setSettleBusy(false);
    }
  };

  const [theme, setTheme] = useState("dark");
  const [, forceTick] = useState(0);
  // Which broker the narrow layout is showing. Above the switch breakpoint both
  // panels are visible side by side and this is ignored (CSS decides), so it
  // costs nothing to keep the state mounted at every width.
  const [activeAcc, setActiveAcc] = useState(0);

  const rootRef = useRef(null);
  const topbarRef = useRef(null);

  /**
   * Publish the sticky top bar's height as --ir-topbar-h.
   *
   * The broker switcher sticks directly beneath the bar on narrow screens, so it
   * needs to know how tall the bar is. That cannot be hardcoded: the bar wraps
   * its controls onto a second row below ~720px (measured 107px there vs ~64px
   * wide), and it also changes height when a warning banner's text reflows. A
   * ResizeObserver keeps the offset correct at every width without a resize
   * listener or a magic number.
   */
  useEffect(() => {
    const bar = topbarRef.current;
    const root = rootRef.current;
    if (!bar || !root) return undefined;

    const apply = () => root.style.setProperty("--ir-topbar-h", `${bar.offsetHeight}px`);
    apply();

    if (typeof ResizeObserver === "undefined") return undefined; // CSS fallback covers it
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [report]);

  // This page is publicly reachable and shows live account figures, so keep
  // it out of search engines. Set on mount rather than in index.html so the
  // rest of the site stays indexable.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow, noarchive";
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  // Re-render once a second so the "checked Ns ago" label stays honest.
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Memoised so the fallback [] is not a fresh array on every render, which
  // would invalidate the maxRows memo below on every tick.
  const title = (report && report.title) || "Managed Trading — Investor Report";
  const reportId = (report && report.reportId) || "";
  const inception = (report && report.inception) || 0;
  const accounts = useMemo(() => (report && report.accounts) || [], [report]);
  const combined = (report && report.combined) || null;

  /**
   * MAY THIS VIEWER SETTLE THE CARRY, and is there anything to settle?
   *
   * Three conditions, each for its own reason:
   *
   *   the SERVER says developer. `viewingAsDeveloper` rides on the payload;
   *   `isDeveloperViewer()` only reads localStorage, which a revoked session, a
   *   demoted user or a stale token all leave looking identical. Drawing a
   *   button the server will refuse is worse than drawing none, so the server's
   *   answer wins and the localStorage hint is the fallback for a payload that
   *   predates this field.
   *
   *   ONE container is selected. Manual settlement acts on a book's carry, and
   *   All spans several books - there is no single carry to bank there.
   *
   *   there IS a carry. Settling nothing is not an action; the route refuses it
   *   with 409, and offering it would only produce that refusal.
   *
   * None of this is the authorisation. The route is guarded with
   * `protect + isDeveloper` and checks the ceiling itself.
   */
  const serverSaysDeveloper =
    report && report.viewingAsDeveloper != null ? Boolean(report.viewingAsDeveloper) : isDeveloperViewer();
  const carriedNow =
    (combined && combined.poolBreakdown && combined.poolBreakdown.carriedIn) || 0;
  /* `selectable` TOO, which confines this to /investor-report.
     /investment-portfolio and -v1 pass an `endpoint`, have no container
     selector, and -v1 shows NORMALISED figures - every size-proportional number
     rescaled onto a fixed capital base. A settle control there would offer a
     ceiling in rescaled money and act on the real book, and those two pages are
     frozen besides. */
  /**
   * TWO CONTROLS, TWO CONDITIONS.
   *
   * The common part — a developer the SERVER agrees is one, on the main report,
   * with one container selected — is shared. What differs is which pot has
   * anything in it, and whether the book allows that pot to be settled by hand.
   *
   * CARRY IS ALWAYS AVAILABLE. It banks profit from periods that have closed,
   * which no account panel on this page is showing, so there is nothing for it
   * to contradict and no switch governs it.
   *
   * PERIOD IS GOVERNED BY THE CONTAINER. `manualSettlementPeriod` rides on the
   * selector payload; the route checks it again and refuses regardless, so this
   * only stops the page drawing a button the server would turn down.
   *
   * The ceilings come from the report payload's own breakdown, so the button
   * appears exactly when the modal would have something to offer. The modal
   * then re-reads them from the route, because the report is a poll behind and
   * the POST checks the fresher figure.
   */
  const settleBase = selectable && serverSaysDeveloper && Boolean(settleUrl) && !showingAll;
  const selectedMeta = containerList.find((c) => c.id === selectedContainer) || null;
  const periodAllowed = selectedMeta ? selectedMeta.manualSettlementPeriod !== false : true;
  /* `settleCarryNow`, `settlePeriodNow` and the two `can…` flags are below,
     immediately after `unsettledTotal` — both limits are derived from it, and a
     `const` cannot be read before its declaration. */
  const ccy = (combined && combined.currency) || "USD";

  /**
   * STANDING PROFIT, INCLUDING WHAT THIS PAIR CARRIED IN.
   *
   * `poolBreakdown.unsettled` is derived from the identity both payload builders
   * publish - carriedIn + earnedThisPair - settledThisPair - so it is the only
   * figure the reconciliation rows can legitimately total to.
   *
   * `combined.unsettledPnl` is the PAIR's own standing profit and excludes the
   * carry, which is right for a per-pair panel and wrong for the lifetime tile
   * and for the total of three book-level rows.
   *
   * Falls back to `unsettledPnl` only when there is no breakdown at all, so a
   * payload shape without it still renders a number rather than "$NaN".
   */
  const unsettledTotal =
    combined && combined.poolBreakdown && combined.poolBreakdown.unsettled != null
      ? combined.poolBreakdown.unsettled
      : combined && combined.unsettledPnl;

  /* THE TWO LIMITS, the same split the server computes, read off the breakdown
     the payload already carries — so each button appears exactly when the modal
     would have something to offer.

     SIGNED, so a book in LOSS offers its deficit on whichever side holds it.
     This used to clamp both at zero, which meant a losing book drew no button
     at all and the loss standing on the tile could not be settled: the one
     thing the control exists for was the one thing it refused. */
  const settleNow = settleLimits(carriedNow, unsettledTotal || 0);
  const settleCarryNow = settleNow.carry;
  const settlePeriodNow = settleNow.period;
  const canSettleCarry = settleBase && hasLimit(settleCarryNow);
  const canSettlePeriod = settleBase && periodAllowed && hasLimit(settlePeriodNow);
  /* Kept for anything still reading the old single flag. */
  const canSettle = canSettleCarry || canSettlePeriod;

  /**
   * WHAT THE MODAL CALLS ITSELF, and it depends on which way the book is
   * running as much as on which button opened it. "Settle carried-in profit"
   * over a deficit is simply wrong, and a reader asked to confirm an amount
   * under a wrong heading has every reason to distrust the figure too.
   */
  const settleLimit = ceilOf(settle, settleScope);
  const settleLoss = settleLimit < 0;
  const settleTitle = settleLoss
    ? settleScope === "period"
      ? "Realise this period\u2019s loss"
      : "Realise the carried-in loss"
    : settleScope === "period"
    ? "Settle this period\u2019s profit"
    : "Settle carried-in profit";
  const settleCheck = checkAmount(settleAmount, settleLimit);

  const maxRows = useMemo(
    () => ({
      positions: Math.max(0, ...accounts.map((a) => (a.positions || []).length)),
      log: Math.max(0, ...accounts.map((a) => (a.log || []).length)),
    }),
    [accounts]
  );

  return (
    <div className={`ir-root ir-theme-${theme}`} data-ir-theme={theme} ref={rootRef}>
      <div className="ir-glow" aria-hidden="true" />

      <header className="ir-topbar" ref={topbarRef}>
        <div className="ir-topbar-inner">
          <div className="ir-brand">
            <img src="/firstbench-logo.png" alt="" className="ir-brand-logo" />
            <div>
              <div className="ir-brand-name">{title}</div>
              <div className="ir-brand-sub">
                {/* A container with no live snapshot carries no `accounts` array —
                    they belong to the card instead — so this said "connecting…"
                    for as long as the page stayed open, on a page that was not
                    connecting to anything. It names the container instead. */}
                {/* Neither All nor a stored card carries an `accounts` array —
                    All's accounts belong to its individual containers — so this
                    said "connecting…" for as long as the page stayed open, on a
                    page connecting to nothing. Each case names itself. */}
                {accounts.length
                  ? accounts.map((a) => a.label).join(" + ")
                  : showingAll
                  ? `All containers · ${(report.cards || []).length} container${
                      (report.cards || []).length === 1 ? "" : "s"
                    }`
                  : report && report.card
                  ? report.card.name
                  : "connecting…"}{" "}
                ·{" "}
                {reportId}
              </div>
            </div>
          </div>

          <div className="ir-topbar-right">
            {/* Only on the main report. The two portfolio pages read the
                default container and have no selector, by decision. */}
            {selectable && (
              <ContainerSelect
                containers={containerList}
                value={selectedContainer}
                onChange={chooseContainer}
                includeAll
                label="Container"
                id="ir-container"
              />
            )}

            {/* "reconnecting" is a LIE whenever the page is polling by design,
                and that is any container the poller is not currently running:
                the live stream carries whichever container the poller has, so
                pointing it at another would replace the figures on screen
                without saying so. The condition is `polling`, from the hook
                that made the decision — not "is this the default container",
                which is a rule the page cannot correctly evaluate. */}
            <span
              className={`ir-live${live ? "" : " is-off"}`}
              title={live ? "Streaming" : polling ? "Refetching on a timer" : "Reconnecting"}
            >
              <span className="ir-live-dot" aria-hidden="true" />
              {live ? `checked ${ago(checkedAt)}` : polling ? `polled ${ago(checkedAt)}` : "reconnecting…"}
            </span>

            <button type="button" className="ir-btn" onClick={refresh} title="Refetch history now">
              <RefreshCw size={15} aria-hidden="true" />
            </button>

            <button
              type="button"
              className="ir-btn ir-btn-icon"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      <main className="ir-main">
        {error && (
          <div className="ir-banner is-error">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}


        {dismissError && (
          <div className="ir-banner is-error">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>{dismissError}</span>
          </div>
        )}

        {report &&
          (report.warnings || [])
            /* Hidden the moment it is clicked, then really gone once the server
               re-sends the report without it. */
            .filter((w) => !(w.dismissKey && justCleared.includes(w.dismissKey)))
            .map((w) => (
              <div className="ir-banner is-warn" key={w.code + w.message}>
                <AlertTriangle size={15} aria-hidden="true" />
                <span>{w.message}</span>
                {/* Only a notice that PUBLISHES a key can be cleared, and only a
                    developer sees the button. A live condition — an account
                    offline right now — carries no key on purpose: hiding it would
                    hide a figure that is currently wrong. */}
                {canDismiss && w.dismissKey && (
                  <button
                    type="button"
                    className="ir-banner-x"
                    onClick={() => dismissNotice(w.dismissKey)}
                    title="Clear this notice for everyone viewing this page"
                    aria-label="Clear this notice for everyone viewing this page"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}

        {!report && !error && (
          <div className="ir-card">
            <p className="ir-empty">
              Connecting to the trading accounts…
              {poller && poller.restarts ? ` (poller restarts: ${poller.restarts})` : ""}
            </p>
          </div>
        )}

        {/* A CONTAINER WITH NO LIVE SNAPSHOT. Its usages, deals, settlements and
            opening capital are all stored, so the honest answer is those figures
            plus when they were last true. Before this branch existed such a
            payload rendered NOTHING AT ALL: `combined` is absent, so the block
            below is skipped, and the "connecting" card is gated on `!report` —
            a report carrying only a card and a message satisfied neither, and
            the page came up blank with a correct-looking header. Found by
            opening the link, not by reading the payload. */}
        {/* ALL CONTAINERS. A separate component because the two views share
            almost nothing — All has no positions and no trade log, and
            `combined` carries a different shape, so the single-container block
            below would read every field All does not have — but built from THIS
            page's parts, so it reads as the same page one level up. */}
        {showingAll && report && report.combined && (
          <AllContainersView report={report} ccy={ccy} inception={inception} />
        )}

        {!showingAll && !combined && report && report.card && (
          <>
            {report.message && (
              <div className="ir-banner is-warn">
                <AlertTriangle size={15} aria-hidden="true" />
                <span>{report.message}</span>
              </div>
            )}
            <div className="ir-ccards">
              <ContainerCard card={report.card} ccy={ccy} />
            </div>
          </>
        )}

        {!showingAll && combined && (
          <>
            <section className="ir-combined" aria-labelledby="ir-comb-t">
              <div className="ir-comb-hero">
                <span className="ir-eyebrow">
                  <Layers size={13} aria-hidden="true" /> Combined ·{" "}
                  {combined.connectedCount}/{combined.accountCount} live
                </span>
                <h1 id="ir-comb-t" className="ir-hero-figure">
                  {money(combined.equity, ccy, 2)}
                </h1>
                <div className="ir-hero-deltas">
                  <Delta value={combined.returnPct} />
                  <span className="ir-hero-abs">{signed(combined.totalPnl, ccy, 2)} total P&amp;L</span>
                </div>
                {/* One chip per account, PAST ONES INCLUDED — the headline equity
                    above sums every account, so listing only the panelled pair
                    left the chips visibly short of it (52,187 shown against a
                    61,278 headline) with nothing to explain the difference. */}
                <div className="ir-comb-chips">
                  {accounts.map((a, i) => (
                    <span key={a.id} className={`ir-chip ir-chip-${i}`}>
                      <b>{a.label}</b>
                      {money(a.equity, a.currency || ccy, 0)}
                      <em className={`ir-${dir(a.periods && a.periods.today.pnl)}`}>
                        {signed((a.periods && a.periods.today.pnl) || 0, a.currency || ccy, 0)}
                      </em>
                    </span>
                  ))}
                  {(combined.pastAccounts || []).map((p) => (
                    <span key={p.id} className="ir-chip ir-chip-past">
                      <b>{p.label}</b>
                      {money(p.equity, ccy, 0)}
                      <em className="ir-muted">past</em>
                    </span>
                  ))}
                </div>
                <div className="ir-trust">
                  <BadgeCheck size={14} aria-hidden="true" />
                  Live read-only feed · figures in {ccy}
                </div>
              </div>

              <div className="ir-comb-figs">
                <Figure label="Total deposited" value={money(combined.deposits, ccy)} />
                <Figure label="Total withdrawn" value={money(combined.withdrawals, ccy)} cls="ir-down" />
                <Figure label="Net deposits" value={money(combined.netDeposits, ccy)} />
                <Figure label="Balance" value={money(combined.balance, ccy)} />
                {/* Base for the headline percentage above. */}
                <Figure
                  label="Opening balance"
                  value={money(combined.openingBalance, ccy)}
                  sub="period start"
                />
                <Figure label="Closed P&L" value={signed(combined.closedPnl, ccy)} cls={`ir-${dir(combined.closedPnl)}`} />
                <Figure label="Floating P&L" value={signed(combined.floating, ccy)} cls={`ir-${dir(combined.floating)}`} />
                <Figure
                  label="Open positions"
                  value={String(combined.openPositions)}
                  sub={`${(combined.openLots || 0).toFixed(2)} lots`}
                />
                <Figure label="Margin used" value={money(combined.margin, ccy)} sub={`free ${compact(combined.freeMargin, ccy)}`} />
                {/* Sits immediately BEFORE Settled P&L so the pair reads together
                    and Settled stays the final card. Realised profit not yet
                    withdrawn — i.e. what is still available to settle. Names the
                    past-account share, which has no panel of its own to show it. */}
                <Figure
                  label="Unsettled P&L"
                  /* poolBreakdown.unsettled, NOT combined.unsettledPnl. The two
                     differ by whatever this pair carried in and has not banked,
                     and the sub-label below says "incl. X carried in" - which
                     was a contradiction while the value beside it excluded that
                     X. Both payload builders now derive this field from the
                     identity, so the tile and the reconciliation underneath it
                     cannot disagree. */
                  value={signed(unsettledTotal, ccy)}
                  cls={`ir-${dir(unsettledTotal)}`}
                  /* From poolBreakdown, not from the live accounts: the pool
                     spans every account ever used, and a retired account's live
                     figures are empty now that per-account scope is the pair
                     date. Reading pastUnsettled here showed "not yet withdrawn"
                     beside a five-figure balance. */
                  sub={
                    combined.poolBreakdown && combined.poolBreakdown.carriedIn
                      ? `incl. ${signed(combined.poolBreakdown.carriedIn, ccy, 0)} carried in`
                      : "not yet withdrawn"
                  }
                  /* TWO CONTROLS, ONE PER POT. Shown only to a signed-in
                     developer, only when a single container is selected, and
                     each only when its own pot has something in it — the action
                     does not exist otherwise, so neither should the control.
                     `viewingAsDeveloper` comes from the server and beats the
                     localStorage hint: a stale token leaves the same storage
                     behind, and a button the server will refuse is worse than
                     none.

                     TWO BUTTONS RATHER THAN ONE WITH A CHOICE INSIDE, as asked:
                     which pots can be settled is then visible without opening
                     anything. "Settle period…" is additionally governed by the
                     container's own switch; the carry is always settleable. */
                  action={
                    canSettleCarry || canSettlePeriod ? (
                      <span className="ir-fig-actions">
                        {/* THE LABEL FOLLOWS THE BOOK. "Settle carried…" over
                            a deficit would read as banking profit that is not
                            there; what the button actually does then is
                            realise the loss, and it says so. */}
                        {canSettleCarry && (
                          <button
                            type="button"
                            className="ir-fig-action"
                            onClick={() => openSettle("carry")}
                            title={
                              settleCarryNow < 0
                                ? "Realise some or all of the carried-in loss"
                                : "Bank some or all of the carried-in profit"
                            }
                          >
                            {settleCarryNow < 0 ? "Settle carried loss…" : "Settle carried…"}
                          </button>
                        )}
                        {canSettlePeriod && (
                          <button
                            type="button"
                            className="ir-fig-action"
                            onClick={() => openSettle("period")}
                            title={
                              settlePeriodNow < 0
                                ? "Realise some or all of this period's loss"
                                : "Bank some or all of this period's profit"
                            }
                          >
                            {settlePeriodNow < 0 ? "Settle period loss…" : "Settle period…"}
                          </button>
                        )}
                      </span>
                    ) : null
                  }
                />
                {/* LAST card, deliberately — both brokers' settlements together,
                    PLUS any profit carried forward from a retired account. Shows
                    the total, and names the carried part so the figure is never
                    larger than the visible panels explain without saying why. */}
                <Figure
                  label="Settled P&L"
                  value={signed(combined.totalSettledPnl, ccy)}
                  cls={`ir-${dir(combined.totalSettledPnl)}`}
                  /* Counted from the settlement history, which is the same
                     replay the figure itself comes from. The old version counted
                     the LIVE accounts' withdrawals, which are zero once the
                     per-account scope is the pair date — so a banked $10,099.77
                     was captioned "not yet settled". */
                  /* COUNTS WITHDRAWALS, not rows. The history now also holds
                     manual settlements, which bank profit with nothing
                     withdrawn - counting them here would caption the card "3
                     withdrawals" when two people took money out and one figure
                     was banked by hand. */
                  sub={
                    (combined.settlementHistory || []).filter((x) => !x.manual).length
                      ? `${(combined.settlementHistory || []).filter((x) => !x.manual).length} withdrawal${
                          (combined.settlementHistory || []).filter((x) => !x.manual).length === 1 ? "" : "s"
                        } banked`
                      : "not yet settled"
                  }
                />
              </div>
            </section>

            <section className="ir-card">
              <div className="ir-block-head">
                <h3>
                  <Wallet size={15} aria-hidden="true" /> Combined statistics
                </h3>
                {/* These statistics span EVERY account ever used, from the overall
                    start date — a wider scope than the money figures above, which
                    cover only the current pair. Saying so is what stops "This
                    month" looking inconsistent with "Closed P&L". */}
                <span className="ir-block-note">
                  {combined.statsScope
                    ? `all accounts since ${serverTime(combined.statsScope.since).date}` +
                      ` · ${combined.statsScope.usageCount} account periods`
                    : "both accounts together"}
                </span>
              </div>
              <PeriodStats periods={combined.periods} ccy={ccy} size="md" inception={inception} />
            </section>

            {/* Where Settled and Unsettled come from.
                Collapsed by default: it answers a question most readers will not
                ask, but when a figure looks wrong it is the only thing that
                explains it. The reconciliation matters because the pool spans
                accounts that have no panel — without these three lines, Unsettled
                is a number nothing else on the page accounts for. */}
            {combined.poolBreakdown && (
              <details className="ir-settle">
                <summary>
                  <span className="ir-settle-title">Settled &amp; unsettled — how these are made up</span>
                  <span className="ir-settle-count">
                    {(combined.settlementHistory || []).length} settlement
                    {(combined.settlementHistory || []).length === 1 ? "" : "s"}
                  </span>
                </summary>

                <div className="ir-settle-body">
                  <ul className="ir-recon">
                    <li>
                      <span>Carried in from earlier accounts</span>
                      <b className={`ir-num ir-${dir(combined.poolBreakdown.carriedIn)}`}>
                        {signed(combined.poolBreakdown.carriedIn, ccy)}
                      </b>
                    </li>
                    <li>
                      <span>Earned by the current pair</span>
                      <b className={`ir-num ir-${dir(combined.poolBreakdown.earnedThisPair)}`}>
                        {signed(combined.poolBreakdown.earnedThisPair, ccy)}
                      </b>
                    </li>
                    {/* Without this line the figures do not reconcile: whatever a
                        withdrawal banked has left the unsettled pool.

                        Shown as it IS, not negated. It used to render
                        `-settledThisPair` so the four rows would add up on the page,
                        which works while the pair is in profit - settle +12,159.75 and
                        the row reads -12,159.75, correctly "profit left the pool". A
                        settled LOSS inverted it into nonsense: a banked -6 displayed as
                        a green +$6.00 under a label saying money was withdrawn, when
                        49,953 had been withdrawn and none of it was profit. */}
                    <li>
                      <span>Banked by withdrawals</span>
                      <b className={`ir-num ir-${dir(combined.poolBreakdown.settledThisPair)}`}>
                        {signed(combined.poolBreakdown.settledThisPair || 0, ccy)}
                      </b>
                    </li>
                    {/* An account swapped out DURING the pair keeps its usage under the
                        same pair date, so its trades and its withdrawal are still part
                        of these totals - but it has no panel any more. Named here, or
                        the two visible panels read "settled 0" and "+41" beneath a pair
                        that settled -6 with nothing accounting for the difference. */}
                    {(combined.retiredAccounts || []).length ? (
                      <li className="ir-recon-sub">
                        <span>
                          includes{" "}
                          {combined.retiredAccounts
                            .map((a) => `${a.label || "#" + a.login} (#${a.login})`)
                            .join(", ")}
                          , replaced during this pair
                        </span>
                        <b className={`ir-num ir-${dir(combined.retiredInPairPnl)}`}>
                          {signed(combined.retiredInPairPnl, ccy)}
                        </b>
                      </li>
                    ) : null}
                    {/* The total of the three rows above, and it has to be the
                        SAME field they came from. Reading combined.unsettledPnl
                        here put a pair-scoped figure under three book-level ones
                        and the column did not sum. */}
                    <li className="ir-recon-total">
                      <span>Unsettled now — not yet withdrawn</span>
                      <b className={`ir-num ir-${dir(unsettledTotal)}`}>
                        {signed(unsettledTotal, ccy)}
                      </b>
                    </li>
                  </ul>

                  {(combined.settlementHistory || []).length === 0 ? (
                    <p className="ir-empty">No withdrawals yet, so nothing has been settled.</p>
                  ) : (
                    <div className="ir-tbl-wrap">
                      <table className="ir-tbl ir-tbl-settle">
                        <caption className="ir-sr">
                          Every withdrawal that banked profit, newest first.
                        </caption>
                        {/* Account column widened: it now carries the login and
                            the pair date under the name. */}
                        <colgroup>
                          <col style={{ width: "19%" }} />
                          <col style={{ width: "27%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "13%" }} />
                          <col style={{ width: "12%" }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col">Date</th>
                            <th scope="col">Account</th>
                            <th scope="col" className="ir-r">Withdrawn</th>
                            {/* "Settled P&L", not "Profit settled": a withdrawal made
                                while the pair is down banks a LOSS, and this column
                                shows it as a negative. */}
                            <th scope="col" className="ir-r">Settled P&amp;L</th>
                            <th scope="col" className="ir-r">Capital back</th>
                            <th scope="col" className="ir-r">Running total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {combined.settlementHistory.map((s) => (
                            /* KEYED ON THE MANUAL ID where there is one. A manual
                               settlement has no login, so `${s.login}-${s.at}`
                               becomes "null-<at>" and two banked at the same
                               effective instant - which is the norm, they all
                               land one second before the boundary - would
                               collide and React would reuse one row for both. */
                            <tr
                              key={s.manual ? `m-${s.manualId}` : `${s.login}-${s.at}`}
                              className={s.manual ? "ir-row-manual" : ""}
                            >
                              <td className="ir-r">
                                <DateTime unix={s.at} />
                              </td>
                              {/* Name, then the login — and the pair date it
                                  belonged to, because the same login can appear
                                  under more than one period and the amounts
                                  would otherwise be indistinguishable.

                                  A MANUAL ROW HAS NEITHER. It belongs to no
                                  account and to no period by construction, so it
                                  says what it is instead of printing "#null" and
                                  a blank date. */}
                              <td>
                                {s.manual ? (
                                  <>
                                    <span className="ir-sym">Manual settlement</span>
                                    <span className="ir-ticket ir-acc-idline">
                                      no withdrawal · banked from carried
                                      {s.by ? ` · ${s.by}` : ""}
                                      {s.note ? ` · ${s.note}` : ""}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="ir-sym">{s.label || `#${s.login}`}</span>
                                    <span className="ir-ticket ir-acc-idline">
                                      #{s.login}
                                      {s.usageId && s.usageId.includes("_")
                                        ? ` · from ${serverTime(
                                            Date.parse(`${s.usageId.split("_")[1]}T00:00:00Z`) / 1000
                                          ).date}`
                                        : ""}
                                    </span>
                                  </>
                                )}
                              </td>
                              {/* An em dash, not 0.00: nothing was withdrawn, and
                                  a zero in a money column reads as a withdrawal
                                  of nothing rather than as no withdrawal. */}
                              <td className="ir-r ir-num">
                                {s.manual ? "—" : money(s.withdrawn, ccy)}
                              </td>
                              <td className={`ir-r ir-num ir-bold ir-${dir(s.settled)}`}>
                                {signed(s.settled, ccy)}
                              </td>
                              <td className="ir-r ir-num ir-xs">
                                {s.manual ? "—" : money(s.capitalReturned, ccy)}
                              </td>
                              <td className="ir-r ir-num">{signed(s.runningTotal, ccy)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="3">Settled P&amp;L</td>
                            {/* totalSettledPnl, not settledPnl. The rows above span
                                every pair, so the footer must total the same ledger.
                                settledPnl is the CURRENT pair only and read 0.00
                                under a table summing 47,307.52. */}
                            <td className={`ir-r ir-num ir-${dir(combined.totalSettledPnl)}`}>
                              {signed(combined.totalSettledPnl, ccy)}
                            </td>
                            <td />
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  <p className="ir-settle-note">
                    Only the profit inside a withdrawal is settled — the rest is your own
                    capital coming back. Changing to a new pair of accounts settles nothing;
                    the unsettled amount carries forward.
                  </p>
                  {/* Withdrawn = Settled + Capital back holds on every row EXCEPT one
                      taken while the pair is down: a banked loss consumes capital rather
                      than paying anything out, so the whole withdrawal is capital back and
                      the loss sits beside it. Said plainly here, because a row that
                      visibly does not add up otherwise reads as an error. */}
                  <p className="ir-settle-note">
                    A withdrawal taken while the pair is down banks that loss: it pays nothing
                    out, so the full amount is capital back and the loss shows beside it. On
                    those rows Settled P&amp;L is not part of the cash withdrawn.
                  </p>
                </div>
              </details>
            )}

            {/* Narrow screens show ONE broker at a time with this switcher
                rather than stacking both, which made the page enormous to
                scroll. CSS hides it above the switch breakpoint, where both
                panels sit side by side and `active` is ignored. */}
            {accounts.length > 1 && (
              <div className="ir-acc-switch" role="tablist" aria-label="Choose broker">
                {accounts.map((a, i) => (
                  <button
                    key={a.id}
                    type="button"
                    role="tab"
                    id={`${a.id}-tab`}
                    aria-selected={i === activeAcc}
                    aria-controls={`${a.id}-panel`}
                    className={`ir-acc-tab ir-acc-tab-${i}${i === activeAcc ? " is-on" : ""}`}
                    onClick={() => setActiveAcc(i)}
                  >
                    <span className="ir-acc-tab-name">{a.label}</span>
                    <span className={`ir-acc-tab-val ir-${dir(a.totalPnl)}`}>
                      {signed(a.totalPnl, ccy, 0)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="ir-split">
              {accounts.map((a, i) => (
                <AccountPanel
                  key={a.id}
                  acc={a}
                  index={i}
                  maxRows={maxRows}
                  inception={inception}
                  // Clamped: an account dropping out of the payload must not
                  // leave every panel hidden on a narrow screen.
                  active={i === Math.min(activeAcc, accounts.length - 1)}
                />
              ))}
            </div>
          </>
        )}

        <footer className="ir-footer">
          <div className="ir-footer-brand">
            <img src="/firstbench-logo.png" alt="" />
            <div>
              <b>{title}</b>
              <span>
                {reportId} · live feed
                {inception ? ` · reporting from ${serverTime(inception).date}` : ""}
                {poller && poller.changedAgo != null ? ` · last change ${poller.changedAgo}s ago` : ""}
              </span>
            </div>
          </div>
          <p className="ir-disclaimer">
            Figures are read live from the brokers&apos; servers on a read-only connection. Trading on margin
            carries a high level of risk and can result in the loss of all capital. Past performance is not
            indicative of future results. Open positions are marked to the broker&apos;s last quoted price;
            all times are broker server time. This document is for the named recipient only and is not an
            offer to buy or sell any financial instrument.
          </p>
        </footer>
      </main>

      {/* ------------------------ MANUAL SETTLEMENT ------------------------
          Opened from the Unsettled tile. Everything it needs comes from the
          route rather than from the report payload, because the ceiling shown
          has to be the one the POST will check against - the report is a poll
          behind, and offering a maximum that is then refused is the one thing a
          modal like this must not do.

          Rendered outside <main> so it overlays the page rather than sitting in
          the document flow. */}
      {settle && (
        <div
          className="ir-modal-back"
          role="dialog"
          aria-modal="true"
          aria-label={settleTitle}
        >
          <div className="ir-modal">
            <div className="ir-modal-head">
              <h2>{settleTitle}</h2>
              <button
                type="button"
                className="ir-btn ir-btn-icon"
                onClick={() => { setSettle(null); setArmedUndo(""); }}
                aria-label="Close"
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>

            <div className="ir-modal-body">
              {settle.failed ? (
                <p className="ir-modal-warn">{settleError || "Could not read the carried figure."}</p>
              ) : (
                <>
                  {/* WHAT IS AND IS NOT ON OFFER, said before the input. The
                      distinction is the whole rule, and a reader who does not
                      know it would read the ceiling as arbitrary. */}
                  {/* BOTH POTS, ALWAYS, whichever button opened this. The
                      ceiling is only meaningful next to the thing it is not:
                      a reader who sees "most you can settle 300" with no sign
                      that 1,000 sits in the other pot reads the 300 as the
                      whole book. */}
                  <dl className="ir-modal-facts">
                    <div>
                      <dt>Unsettled in total</dt>
                      <dd className="ir-num">{signed(settle.pot, ccy)}</dd>
                    </div>
                    <div>
                      <dt>Carried in</dt>
                      <dd className={`ir-num${settleScope === "carry" ? " ir-bold" : ""}`}>
                        {money(settle.carryCeiling, ccy)}
                      </dd>
                    </div>
                    <div>
                      <dt>This period</dt>
                      <dd className={`ir-num${settleScope === "period" ? " ir-bold" : ""}`}>
                        {money(settle.periodCeiling, ccy)}
                      </dd>
                    </div>
                    <div>
                      <dt>Already banked by hand</dt>
                      <dd className="ir-num">{money(settle.manualSettled, ccy)}</dd>
                    </div>
                    <div>
                      {/* "Most you can settle" over a deficit would read as a
                          cap on profit. What it bounds then is how much of the
                          loss may be realised, which is a different sentence. */}
                      <dt>{settleLoss ? "Loss you can realise" : "Most you can settle"}</dt>
                      <dd className="ir-num ir-bold">{money(settleLimit, ccy)}</dd>
                    </div>
                  </dl>

                  <p className="ir-modal-note">
                    {settleScope === "period" ? (
                      settleLoss ? (
                        <>
                          This realises a loss made <b>since the period began</b> — the same thing a
                          withdrawal does to a book under water, done deliberately. Settled falls by
                          the amount and unsettled rises toward zero. The account panels will move;
                          balances do not change.
                        </>
                      ) : (
                        <>
                          This banks profit earned <b>since the period began</b>. The account panels
                          will move: settled rises and unsettled falls by the same amount on each
                          account, and the opening balance rises with it. Balances do not change.
                        </>
                      )
                    ) : settleLoss ? (
                      <>
                        This realises a loss carried in from periods that have{" "}
                        <b>already closed</b>. Settled falls by the amount and unsettled rises toward
                        zero. This period&apos;s own result stays unsettled, so the figures on the
                        account panels do not move.
                      </>
                    ) : (
                      <>
                        Only profit carried in from periods that have closed can be banked here. This
                        period&apos;s own profit stays unsettled, so the figures on the account
                        panels do not move.
                      </>
                    )}
                    {settle.pairStartDate ? (
                      <>
                        {" "}
                        Settled against the {settle.pairStartDate}
                        {settle.pairStartTime ? ` ${settle.pairStartTime}` : ""} changeover.
                      </>
                    ) : null}
                  </p>

                  <label className="ir-modal-field" htmlFor="ir-settle-amount">
                    <span>{settleLoss ? "How much of the loss to realise" : "How much to settle"}</span>
                    <input
                      id="ir-settle-amount"
                      type="number"
                      step="0.01"
                      /* The stepper's own bounds, which run from zero to the
                         limit in whichever direction the book is going — so a
                         losing book steps between the deficit and zero rather
                         than refusing to go below it. A convenience, not the
                         check: a typed value can still fall outside, which is
                         why the button disables below and the route refuses
                         after that. */
                      min={settleLoss ? settleLimit : 0}
                      max={settleLoss ? 0 : settleLimit}
                      value={settleAmount}
                      onChange={(e) => setSettleAmount(e.target.value)}
                    />
                  </label>

                  {/* WHY THE BUTTON IS DISABLED, named while it is being typed
                      rather than on submit. Without this the reader is left
                      with a dead button and no reason for it. */}
                  {settleCheck.tooMuch && (
                    <p className="ir-modal-warn">
                      That is more than the {money(settleLimit, ccy)}{" "}
                      {settleLoss
                        ? settleScope === "period"
                          ? "this period has lost"
                          : "carried in"
                        : settleScope === "period"
                        ? "this period has earned"
                        : "carried in"}
                      . The rest of unsettled belongs to the other pot and cannot be settled here.
                    </p>
                  )}
                  {/* THE SIGN, which is the whole difference on a losing book.
                      A reader who types 400 against a deficit has asked to bank
                      profit that is not there, and the engine would settle
                      nothing at all — so it is named here rather than accepted
                      and quietly dropped. */}
                  {settleCheck.wrongWay && (
                    <p className="ir-modal-warn">
                      {settleLoss ? (
                        <>
                          This book is holding a loss of {money(Math.abs(settleLimit), ccy)}, so the
                          amount has to be <b>negative</b> — it realises the loss rather than banking
                          profit. Type −{Math.abs(Number(settleAmount)).toFixed(2)} to settle that much
                          of it.
                        </>
                      ) : (
                        <>
                          This book is holding {money(settleLimit, ccy)} of profit, so the amount has
                          to be positive. Only a book under water is settled with a negative amount.
                        </>
                      )}
                    </p>
                  )}
                  {settleCheck.zero && <p className="ir-modal-warn">Zero settles nothing.</p>}
                  {settleCheck.exhausted && (
                    <p className="ir-modal-note">
                      Nothing left in this pot. The book holds {signed(settle.pot, ccy)} unsettled
                      {Math.abs(
                        settleScope === "period" ? settle.carryCeiling : settle.periodCeiling
                      ) > 0.004
                        ? `, all of it ${settleScope === "period" ? "carried in" : "from this period"}`
                        : ""}
                      .
                    </p>
                  )}
                  {settleCheck.ok && (
                    <p className="ir-modal-note">
                      {money(settleLimit - settleCheck.n, ccy)} would stay{" "}
                      {settleScope === "period" ? "in this period" : "carried"}.
                    </p>
                  )}

                  <label className="ir-modal-field" htmlFor="ir-settle-note">
                    <span>Note (optional)</span>
                    <input
                      id="ir-settle-note"
                      type="text"
                      value={settleNote}
                      placeholder="why this was settled by hand"
                      onChange={(e) => setSettleNote(e.target.value)}
                    />
                  </label>

                  {settleError && <p className="ir-modal-warn">{settleError}</p>}
                  {settleDone && <p className="ir-modal-done">{settleDone}</p>}

                  {/* WHAT HAS ALREADY BEEN DONE, and how to undo it. A manual
                      settlement is the only figure here that exists because
                      somebody decided it should, so it is the only one that has
                      to name who and when. */}
                  {(settle.manualSettlements || []).length > 0 && (
                    <div className="ir-modal-list">
                      <h3>Settled by hand</h3>
                      <table className="ir-modal-table">
                        <tbody>
                          {settle.manualSettlements.map((m) => {
                            const ageMs = m.createdAt ? Date.now() - Date.parse(m.createdAt) : Infinity;
                            /* Under twelve hours counts as "the one I just made"
                               — about a working session. Anything older is
                               history and arms first. */
                            const fresh = Number.isFinite(ageMs) && ageMs < 12 * 3600 * 1000;
                            const armed = armedUndo === m.id;
                            return (
                              <tr key={m.id} className={armed ? "ir-undo-armed" : ""}>
                                <td className="ir-num">{money(m.amount, ccy)}</td>
                                <td className="ir-xs">
                                  {armed ? (
                                    /* SAYS WHAT IT WILL DO, in the two figures
                                       that actually move. "Are you sure" tells
                                       the reader nothing they did not already
                                       know. */
                                    <span className="ir-undo-what">
                                      Moves {money(m.amount, ccy)} back into{" "}
                                      {(m.scope || "carry") === "period" ? "this period" : "carried"} and
                                      {m.amount < 0 ? " raises" : " lowers"} Settled by the same.
                                    </span>
                                  ) : (
                                    <>
                                      {m.by || "developer"}
                                      {m.createdAt ? ` · ${String(m.createdAt).slice(0, 10)}` : ""}
                                      {m.note ? ` · ${m.note}` : ""}
                                    </>
                                  )}
                                </td>
                                <td className="ir-r ir-undo-cell">
                                  {armed ? (
                                    <>
                                      <button
                                        type="button"
                                        className="ir-btn ir-btn-danger"
                                        disabled={settleBusy}
                                        onClick={() => {
                                          setArmedUndo("");
                                          removeSettle(m.id);
                                        }}
                                      >
                                        {settleBusy ? "Undoing…" : "Confirm"}
                                      </button>
                                      <button
                                        type="button"
                                        className="ir-btn"
                                        onClick={() => setArmedUndo("")}
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="ir-btn"
                                      disabled={settleBusy}
                                      onClick={() => (fresh ? removeSettle(m.id) : setArmedUndo(m.id))}
                                      title={
                                        fresh
                                          ? "Remove this settlement — the amount returns to carried"
                                          : "This one is not from today — you will be asked to confirm"
                                      }
                                    >
                                      {fresh ? "Undo" : "Undo?"}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="ir-modal-foot">
              <button type="button" className="ir-btn" onClick={() => { setSettle(null); setArmedUndo(""); }}>
                Close
              </button>
              {!settle.failed && (
                <button
                  type="button"
                  className="ir-btn ir-btn-primary"
                  disabled={settleBusy || !settleCheck.ok}
                  onClick={submitSettle}
                >
                  {settleBusy ? "Settling…" : settleLoss ? "Realise the loss" : "Settle"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
